import { motion } from "framer-motion";
import { BookMarked, Quote } from "lucide-react";
import PageHeader from "../components/PageHeader";
import CommentsSection from "../components/CommentsSection";
import { currentRead, pastReads } from "../data/content";

export default function Books() {
  return (
    <div data-testid="page-books">
      <PageHeader
        eyebrow="The Bookshelf"
        title="Books I'm reading. Books I keep returning to."
        intro="A slow, honest list. I read for the pleasure of it — sometimes one chapter a night, sometimes two hundred pages on a Sunday. Below: what is on the bedside table, and the books I have already lived inside this year."
        testid="books-header"
      />

      {/* Current read */}
      <section className="pb-10">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-sm border border-[#D8D3CC] bg-[#F2EFEB] overflow-hidden grid md:grid-cols-12"
            data-testid="current-read-card"
          >
            <div className="md:col-span-5 lg:col-span-4 relative">
              <img
                src={currentRead.cover}
                alt={`${currentRead.title} by ${currentRead.author}`}
                className="w-full h-72 md:h-full object-cover"
              />
            </div>
            <div className="md:col-span-7 lg:col-span-8 p-8 sm:p-12 flex flex-col">
              <div className="flex items-center gap-2">
                <BookMarked size={18} className="text-[#CC7A52]" strokeWidth={1.6} />
                <span className="eyebrow !text-[#CC7A52]">Currently reading</span>
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl mt-4 text-[#2C2A28] leading-tight">
                {currentRead.title}
              </h2>
              <p className="mt-2 text-[#5C5852] italic">
                by {currentRead.author} · <span className="not-italic">{currentRead.progress}</span>
              </p>
              <div className="mt-6 flex gap-3 items-start text-[#2C2A28]">
                <Quote size={20} className="text-[#4A6B53] mt-1 shrink-0" strokeWidth={1.4} />
                <p className="text-lg leading-relaxed text-[#2C2A28]">
                  {currentRead.thoughts}
                </p>
              </div>
              <CommentsSection
                page="Books"
                itemId={`current:${currentRead.title}`}
                itemTitle={currentRead.title}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Past reads */}
      <section className="py-16 sm:py-20">
        <div className="container-narrow">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <p className="eyebrow">Past reads</p>
              <h2 className="font-serif text-4xl sm:text-5xl mt-3 text-[#2C2A28]">
                The shelf, with notes.
              </h2>
            </div>
            <p className="text-[#5C5852] text-sm max-w-md">
              Short summaries and the honest aftertaste. Not reviews — more like
              postcards I would write to a friend who asked.
            </p>
          </div>

          <div className="grid gap-5 lg:grid-cols-2" data-testid="past-reads-grid">
            {pastReads.map((b, i) => (
              <motion.article
                key={b.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="card-lift rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] overflow-hidden flex flex-col sm:flex-row"
                data-testid={`past-read-${i}`}
              >
                {b.cover && (
                  <div className="sm:w-44 shrink-0 flex items-center justify-center p-6 sm:p-7 sm:border-r border-b sm:border-b-0 border-[#D8D3CC] bg-[#EAE5DC]">
                    <img
                      src={b.cover}
                      alt={`Cover of ${b.title}`}
                      loading="lazy"
                      className="max-h-52 sm:max-h-60 w-auto object-contain shadow-[0_8px_18px_-12px_rgba(44,42,40,0.45)]"
                    />
                  </div>
                )}
                <div className="p-7 sm:p-8 flex flex-col flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-serif text-2xl sm:text-3xl text-[#2C2A28] leading-tight">
                      {b.title}
                    </h3>
                    {b.year && (
                      <span className="text-xs text-[#5C5852] tracking-widest">
                        {b.year}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-[#5C5852] italic">
                    by {b.author}
                  </p>
                  <p className="mt-5 text-[#2C2A28] leading-relaxed">
                    {b.summary}
                  </p>
                  <div className="mt-5 pt-5 border-t border-dashed border-[#D8D3CC]">
                    <p className="eyebrow !text-[10px]">My thoughts</p>
                    <p className="mt-2 text-[#5C5852] leading-relaxed">
                      {b.thoughts}
                    </p>
                  </div>
                  <CommentsSection
                    page="Books"
                    itemId={`past:${b.title}`}
                    itemTitle={b.title}
                  />
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
